import { fetchConfig } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const CART_UPGRADE_COMPLETE_EVENT = 'nourishplus:cart-upgrade-complete';

/**
 * @param {string} sectionId
 * @returns {HTMLElement | null}
 */
function getCartSectionRoot(sectionId) {
  if (!sectionId) return null;
  const normalizedId = sectionId.replace(/^shopify-section-/, '');
  return document.getElementById(`shopify-section-${normalizedId}`);
}

/**
 * Reveals the post-mutation confirmation only after Shopify has installed the
 * authoritative cart HTML. This survives both the drawer hydration morph and
 * the cart page's full section morph.
 *
 * @param {{
 *   sectionId: string,
 *   isDrawer: boolean,
 *   setHandle: string,
 *   successTitle: string,
 *   successMessage: string
 * }} transition
 */
async function completeUpgradeExperience(transition) {
  /** @type {HTMLElement | null} */
  let confirmation = null;
  /** @type {HTMLElement | null} */
  let resultItem = null;

  for (let attempt = 0; attempt < 40; attempt += 1) {
    const root = getCartSectionRoot(transition.sectionId);
    confirmation = root?.querySelector('[data-np-cart-upgrade-confirmation]') ?? null;
    resultItem =
      Array.from(root?.querySelectorAll('[data-product-handle]') ?? []).find(
        (element) =>
          element instanceof HTMLElement &&
          !element.hasAttribute('data-np-cart-upgrade-confirmation') &&
          element.dataset.productHandle === transition.setHandle
      ) ?? null;

    if (confirmation) break;
    await new Promise((resolve) => setTimeout(resolve, 50));
  }

  if (!confirmation) return;

  if (transition.isDrawer) {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function' && !drawer.hasAttribute('open')) {
      drawer.open();
    }
  }

  const title = confirmation.querySelector('[data-np-cart-upgrade-confirmation-title]');
  const copy = confirmation.querySelector('[data-np-cart-upgrade-confirmation-copy]');
  if (title) title.textContent = transition.successTitle;
  if (copy) copy.textContent = transition.successMessage;

  confirmation.hidden = false;
  confirmation.dataset.state = 'success';
  confirmation.classList.add('is-visible');
  resultItem?.classList.add('np-cart-upgrade-result');

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  requestAnimationFrame(() => {
    confirmation?.focus({ preventScroll: true });
    confirmation?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  });

  if (resultItem) {
    window.setTimeout(() => resultItem?.classList.remove('np-cart-upgrade-result'), 3200);
  }

  document.dispatchEvent(
    new CustomEvent(CART_UPGRADE_COMPLETE_EVENT, {
      detail: {
        sectionId: transition.sectionId,
        context: transition.isDrawer ? 'drawer' : 'page',
        productHandle: transition.setHandle,
      },
    })
  );
}

/**
 * Replaces an eligible Nourishplus serum and/or cream cart line with one set.
 *
 * Liquid owns eligibility and display arithmetic. This component revalidates the
 * current cart and set price immediately before changing the cart so a stale card
 * cannot remove a line that the shopper has since edited.
 */
class NourishplusCartUpgrade extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {boolean} */
  #pending = false;

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {HTMLElement | null} */
  #status = null;

  connectedCallback() {
    this.#button = this.querySelector('[data-np-cart-upgrade-submit]');
    this.#status = this.querySelector('[data-np-cart-upgrade-status]');
    this.#button?.addEventListener('click', this.#onSwitch);
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onSwitch);
    this.#abortController?.abort();
  }

  /**
   * Called by the document-level click delegate. Delegation is required because
   * the cart drawer's hydration morph can insert this element after page load
   * without replaying script tags or component setup inside the new subtree.
   */
  requestSwitch() {
    this.#button ??= this.querySelector('[data-np-cart-upgrade-submit]');
    this.#status ??= this.querySelector('[data-np-cart-upgrade-status]');
    void this.#onSwitch();
  }

  /**
   * @returns {string[]}
   */
  #expectedLineKeys() {
    return [this.dataset.serumLineKey, this.dataset.creamLineKey].filter(
      /** @returns {value is string} */
      (value) => Boolean(value)
    );
  }

  /**
   * @returns {number[]}
   */
  #expectedVariantIds() {
    return [this.dataset.serumVariantId, this.dataset.creamVariantId]
      .filter(Boolean)
      .map(Number)
      .filter(Number.isInteger);
  }

  /**
   * @returns {string[]}
   */
  #sectionIds() {
    const sectionIds = new Set();
    if (this.dataset.sectionId) sectionIds.add(this.dataset.sectionId);

    for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    }

    return Array.from(sectionIds).slice(0, 5);
  }

  /**
   * @returns {Promise<any>}
   */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal: this.#abortController?.signal,
    });

    if (!response.ok) throw new Error(`Cart refresh failed with status ${response.status}`);
    return response.json();
  }

  /**
   * @param {any} item
   * @returns {string}
   */
  #lineSignature(item) {
    const properties = Object.entries(item.properties ?? {}).sort(([left], [right]) => left.localeCompare(right));
    const sellingPlan =
      item.selling_plan_allocation?.selling_plan?.id ??
      item.selling_plan_allocation?.selling_plan_id ??
      item.selling_plan_allocation?.selling_plan?.name ??
      null;

    return JSON.stringify({
      productId: Number(item.product_id),
      variantId: Number(item.variant_id),
      quantity: Number(item.quantity),
      properties,
      sellingPlan,
    });
  }

  /**
   * @param {any[]} items
   * @returns {string[]}
   */
  #lineSignatures(items) {
    return items.map((item) => this.#lineSignature(item)).sort();
  }

  /**
   * @param {any} cart
   * @returns {string[] | null}
   */
  #freshEligibleLineKeys(cart) {
    const setHandle = this.dataset.setHandle;
    const setVariantId = Number(this.dataset.setVariantId);
    const expectedVariantIds = new Set(this.#expectedVariantIds());
    if (!Array.isArray(cart.items) || expectedVariantIds.size === 0) return null;
    if (
      cart.items.some(
        (item) => item.handle === setHandle || Number(item.variant_id) === setVariantId
      )
    ) {
      return null;
    }

    const qualifyingItems = cart.items.filter((item) => expectedVariantIds.has(Number(item.variant_id)));
    if (qualifyingItems.length !== expectedVariantIds.size) return null;

    const hasEveryExpectedVariant = Array.from(expectedVariantIds).every(
      (variantId) => qualifyingItems.filter((item) => Number(item.variant_id) === variantId).length === 1
    );
    if (!hasEveryExpectedVariant) return null;

    const itemsAreSafe = qualifyingItems.every((item) => {
      const customerProperties = Object.keys(item.properties ?? {}).filter(
        (key) => key !== '__kaching_bundles'
      );
      return (
        Boolean(item.key) &&
        Number(item.quantity) === 1 &&
        customerProperties.length === 0 &&
        !item.selling_plan_allocation &&
        !item.parent_relationship?.parent &&
        (item.item_components?.length ?? 0) === 0 &&
        (item.discounts?.length ?? 0) === 0 &&
        (item.line_level_discount_allocations?.length ?? 0) === 0
      );
    });

    return itemsAreSafe ? qualifyingItems.map((item) => item.key) : null;
  }

  /**
   * @param {any} beforeCart
   * @param {any} updatedCart
   * @param {string[]} removedLineKeys
   * @returns {boolean}
   */
  #isExpectedUpdatedCart(beforeCart, updatedCart, removedLineKeys) {
    const setHandle = this.dataset.setHandle;
    const serumHandle = this.dataset.serumHandle;
    const creamHandle = this.dataset.creamHandle;
    const setVariantId = Number(this.dataset.setVariantId);
    const expectedVariantIds = new Set(this.#expectedVariantIds());
    const qualifyingHandles = new Set([serumHandle, creamHandle]);
    const setItems = updatedCart.items.filter(
      (item) => item.handle === setHandle && Number(item.variant_id) === setVariantId
    );

    if (setItems.length !== 1 || Number(setItems[0].quantity) !== 1) return false;
    if (
      updatedCart.items.some(
        (item) => qualifyingHandles.has(item.handle) || expectedVariantIds.has(Number(item.variant_id))
      )
    ) {
      return false;
    }

    const unrelatedBefore = beforeCart.items.filter((item) => !removedLineKeys.includes(item.key));
    const unrelatedAfter = updatedCart.items.filter((item) => item.handle !== setHandle);
    const beforeSignatures = this.#lineSignatures(unrelatedBefore);
    const afterSignatures = this.#lineSignatures(unrelatedAfter);

    return (
      beforeSignatures.length === afterSignatures.length &&
      beforeSignatures.every((signature, index) => signature === afterSignatures[index])
    );
  }

  /**
   * @param {any} beforeCart
   * @param {any} currentCart
   * @returns {boolean}
   */
  #cartLinesMatch(beforeCart, currentCart) {
    const beforeSignatures = this.#lineSignatures(beforeCart.items);
    const currentSignatures = this.#lineSignatures(currentCart.items);
    return (
      beforeSignatures.length === currentSignatures.length &&
      beforeSignatures.every((signature, index) => signature === currentSignatures[index])
    );
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    if (this.#status) {
      this.#status.textContent = message;
      this.#status.hidden = false;
      this.#status.dataset.state = 'error';
    }
  }

  #setPending(pending) {
    this.#pending = pending;
    if (!this.#button) return;

    this.#button.disabled = pending;
    this.#button.setAttribute('aria-busy', pending ? 'true' : 'false');
    const label = this.#button.querySelector('[data-np-cart-upgrade-label]');
    if (label) {
      label.textContent = pending ? this.dataset.loadingLabel ?? '' : this.dataset.buttonLabel ?? '';
    }
  }

  #hasUnsavedQuantityChanges() {
    const cartSurface = this.closest('cart-items-component, .np5-cart, .np-cart');
    if (!cartSurface) return false;

    return Array.from(cartSurface.querySelectorAll('input[name^="updates"]')).some(
      (input) =>
        input instanceof HTMLInputElement && !this.contains(input) && input.value !== input.defaultValue
    );
  }

  async #refreshOwnSection() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    await sectionRenderer.renderSection(sectionId, {
      cache: false,
      mode: this.closest('cart-items-component[data-drawer]') ? 'hydration' : 'full',
    });
  }

  #reloadAuthoritativeCart() {
    window.location.reload();
  }

  #onSwitch = async (event) => {
    event?.preventDefault();
    if (this.#pending) return;

    const transition = {
      sectionId: this.dataset.sectionId ?? '',
      isDrawer: Boolean(this.closest('cart-items-component[data-drawer]')),
      setHandle: this.dataset.setHandle ?? '',
      successTitle: this.dataset.successTitle ?? '',
      successMessage: this.dataset.successMessage ?? '',
    };

    const setVariantId = this.dataset.setVariantId;
    if (this.#expectedLineKeys().length === 0 || this.#expectedVariantIds().length === 0 || !setVariantId) return;
    if (this.#hasUnsavedQuantityChanges()) {
      this.#showError(this.dataset.unsavedQuantityMessage ?? '');
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#setPending(true);

    if (this.#status) {
      this.#status.textContent = '';
      this.#status.hidden = true;
      delete this.#status.dataset.state;
    }

    /** @type {any | null} */
    let beforeCart = null;
    /** @type {ReturnType<typeof CartLinesUpdateEvent.createPromise> | null} */
    let deferredPromise = null;
    let mutationStarted = false;

    try {
      beforeCart = await this.#fetchCart();
      const removedLineKeys = this.#freshEligibleLineKeys(beforeCart);

      if (!removedLineKeys) {
        await this.#refreshOwnSection();
        return;
      }

      /** @type {Record<string, number>} */
      const updates = {};
      for (const lineKey of removedLineKeys) updates[lineKey] = 0;
      updates[setVariantId] = 1;

      const sections = this.#sectionIds();
      const body = JSON.stringify({
        updates,
        sections: sections.join(','),
        sections_url: window.location.pathname + window.location.search,
      });

      deferredPromise = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update',
          context: 'cart',
          lines: [
            ...removedLineKeys.map((id) => ({ id, quantity: 0 })),
            { merchandiseId: setVariantId, quantity: 1 },
          ],
          promise: deferredPromise.promise,
        })
      );

      mutationStarted = true;
      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body }),
        signal: this.#abortController.signal,
      });
      const responseText = await response.text();
      const updatedCart = JSON.parse(responseText);
      const apiError = updatedCart.errors ?? updatedCart.description ?? updatedCart.message;

      if (!response.ok || apiError) {
        let verifiedCart = null;
        try {
          verifiedCart = await this.#fetchCart();
        } catch {
          deferredPromise.reject(new Error(String(apiError ?? 'Cart update failed')));
          this.#showError(this.dataset.unknownStateMessage ?? '');
          return;
        }

        deferredPromise.reject(new Error(String(apiError ?? 'Cart update failed')));
        if (!this.#cartLinesMatch(beforeCart, verifiedCart)) {
          this.#reloadAuthoritativeCart();
          return;
        }

        this.#showError(this.dataset.errorMessage ?? '');
        return;
      }

      if (!this.#isExpectedUpdatedCart(beforeCart, updatedCart, removedLineKeys)) {
        deferredPromise.reject(new Error('The returned cart did not match the requested switch'));
        this.#reloadAuthoritativeCart();
        return;
      }

      if (this.#status) {
        this.#status.textContent = this.dataset.successMessage ?? '';
        this.#status.hidden = false;
        this.#status.dataset.state = 'success';
      }

      deferredPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
        detail: {
          sections: updatedCart.sections,
          items: updatedCart.items,
          itemCount: updatedCart.item_count,
          source: 'nourishplus-cart-upgrade',
          didError: false,
        },
      });

      const owningCartComponent = this.closest('cart-items-component');
      const sectionId = this.dataset.sectionId;
      const sectionHtml = sectionId ? updatedCart.sections?.[sectionId] : null;

      if (!owningCartComponent) {
        if (!sectionId || !sectionHtml) {
          this.#reloadAuthoritativeCart();
          return;
        }

        try {
          await morphSection(sectionId, sectionHtml, { mode: 'full' });
        } catch {
          this.#reloadAuthoritativeCart();
          return;
        }
      }

      void completeUpgradeExperience(transition);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      deferredPromise?.reject(error);

      if (mutationStarted && beforeCart) {
        try {
          const currentCart = await this.#fetchCart();
          if (!this.#cartLinesMatch(beforeCart, currentCart)) {
            this.#reloadAuthoritativeCart();
            return;
          }
        } catch {
          this.#showError(this.dataset.unknownStateMessage ?? '');
          return;
        }
      }

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Cart update failed',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#showError(this.dataset.errorMessage ?? '');
    } finally {
      this.#setPending(false);
    }
  };
}

if (!customElements.get('nourishplus-cart-upgrade')) {
  customElements.define('nourishplus-cart-upgrade', NourishplusCartUpgrade);
}

document.addEventListener('click', (event) => {
  const button =
    event.target instanceof Element ? event.target.closest('[data-np-cart-upgrade-submit]') : null;
  const component = button?.closest('nourishplus-cart-upgrade');

  if (component) {
    customElements.upgrade(component);
  }

  if (component && typeof component.requestSwitch === 'function') {
    event.preventDefault();
    component.requestSwitch();
  }
}, true);
